/**
 * The Markdown that Documentation articles are written in, parsed into a
 * plain tree of blocks and inline runs - never into HTML.
 *
 * THIS FILE EXISTS TWICE, ON PURPOSE: backend/lib/docs-markdown.js and
 * frontend/src/lib/docsMarkdown.js. The browser draws the tree as React
 * elements (components/docs/DocMarkdown.js); the server uses the same parse
 * for the search text, the links an article makes, and the credential guard.
 * One parser means the warning the editor shows and the refusal the server
 * gives can never disagree. Everything above the "exports" line must be
 * byte-for-byte the same in both files - e2e-docs.js checks it.
 *
 * WHY NOT HTML. components/recording/Markdown.js says it best: nothing here is
 * ever handed to the browser as HTML, so "<script>" in an article is those
 * eight characters in a paragraph. There is no raw-HTML path at all - not a
 * sanitiser that could miss a case, but no door. Links are the one place a
 * string becomes something a browser acts on, so they go through safeHref():
 * an allow-list of http(s), mailto, an article (doc:<id>), one of the
 * article's files (docfile:<id>), an anchor (#x) and a path inside Jarvis
 * (/tickets/...). Anything else - javascript:, data:,
 * vbscript:, file:, protocol-relative //evil - is drawn as plain text.
 * Images come only from the article's own files (docfile:<id>) or https.
 *
 * WHAT IS UNDERSTOOD:
 *   # headings (1-4)          an id each, for the table of contents
 *   paragraphs, **bold**, *italic*, ~~struck~~, `code`, [links](...), ![images](...)
 *   - bullets, 1. numbers, nested by indenting two spaces or more
 *   - [ ] and - [x] task items (runbook steps a reader can tick)
 *   ```lang fenced code       a Copy button in the browser
 *   ```flow                   a workflow: one step per line, "Step | Who | When"
 *   > quotes, and callouts: > [!NOTE] / [!TIP] / [!WARNING] / [!DANGER]
 *   | tables | with | a header row |
 *   --- a rule
 *   JAR-2026-0107             a ticket, drawn as a chip
 *   IT-LAP-000001             an asset tag, drawn as a chip
 */

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const DOC_LINK_RE = new RegExp(`^doc:(${UUID})(#[a-z0-9-]+)?$`, 'i');
const FILE_RE = new RegExp(`^docfile:(${UUID})$`, 'i');
const TICKET_RE = /#?\b(JAR-\d{4}-\d{4,6})\b/;
const ASSET_RE = /\b([A-Z]{2,8}(?:-[A-Z]{2,8})*-\d{6})\b/;
const CALLOUTS = ['note', 'tip', 'warning', 'danger'];

/** A heading's anchor: lower case, words joined by hyphens. */
function slug(text) {
  return String(text || '').toLowerCase()
    .replace(/[`*_~[\]()!#]/g, '')
    .replace(/[^a-z0-9\u00c0-\u024f]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'section';
}

/**
 * Where a link may go, or null for "draw this as text". The one gate between
 * an author's string and something a browser follows.
 */
function safeHref(raw) {
  const href = String(raw || '').trim();
  if (!href || href.length > 2000) return null;
  // Control characters and whitespace inside a scheme are how "java\tscript:"
  // gets past a naive check; nothing legitimate has them.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(href)) return null;
  const doc = DOC_LINK_RE.exec(href);
  if (doc) return { kind: 'doc', id: doc[1].toLowerCase(), anchor: doc[2] ? doc[2].slice(1) : null, href };
  const file = FILE_RE.exec(href);
  if (file) return { kind: 'file', id: file[1].toLowerCase(), href };
  if (/^#[a-z0-9-]{1,80}$/i.test(href)) return { kind: 'anchor', href };
  if (/^\/(?![/\\])[A-Za-z0-9._~!$&'()*+,;=:@%/?#-]*$/.test(href)) return { kind: 'app', href };
  if (/^https?:\/\/[^\s/?#]+[^\s]*$/i.test(href)) return { kind: 'web', href };
  if (/^mailto:[^\s@]+@[^\s@]+$/i.test(href)) return { kind: 'mail', href };
  return null;
}

/** Where a picture may come from, or null. */
function safeSrc(raw) {
  const src = String(raw || '').trim();
  const file = FILE_RE.exec(src);
  if (file) return { kind: 'file', id: file[1].toLowerCase(), src };
  if (/^https:\/\/[^\s/?#]+[^\s"']*$/i.test(src) && src.length <= 2000) return { kind: 'web', src };
  return null;
}

/* ------------------------------------------------------------------ inline */

/**
 * Inline runs, left to right. Returns a list of
 *   { t: 'text', v } | { t: 'b'|'i'|'s', c: [...] } | { t: 'code', v }
 *   { t: 'link', href, kind, id?, c: [...] } | { t: 'img', src, kind, id?, alt }
 *   { t: 'ticket', v } | { t: 'asset', v } | { t: 'br' }
 */
function inline(src, depth = 0) {
  const s = String(src || '');
  const out = [];
  let text = '';
  const flush = () => {
    if (!text) return;
    // Ticket numbers and asset tags inside plain text become chips.
    let rest = text;
    while (rest) {
      const tm = TICKET_RE.exec(rest);
      const am = ASSET_RE.exec(rest);
      const m = tm && (!am || tm.index <= am.index) ? { m: tm, t: 'ticket' } : (am ? { m: am, t: 'asset' } : null);
      if (!m) { out.push({ t: 'text', v: rest }); break; }
      if (m.m.index > 0) out.push({ t: 'text', v: rest.slice(0, m.m.index) });
      out.push({ t: m.t, v: m.m[1] });
      rest = rest.slice(m.m.index + m.m[0].length);
    }
    text = '';
  };
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    const rest = s.slice(i);
    // A backslash escapes the next punctuation character.
    if (ch === '\\' && i + 1 < s.length && /[\\`*_{}[\]()#+\-.!~|>]/.test(s[i + 1])) {
      text += s[i + 1]; i += 2; continue;
    }
    if (ch === '`') {
      const end = s.indexOf('`', i + 1);
      if (end > i + 1) { flush(); out.push({ t: 'code', v: s.slice(i + 1, end) }); i = end + 1; continue; }
    }
    if (depth < 4 && (rest.startsWith('**') || rest.startsWith('__'))) {
      const mark = rest.slice(0, 2);
      const end = s.indexOf(mark, i + 2);
      if (end > i + 2) { flush(); out.push({ t: 'b', c: inline(s.slice(i + 2, end), depth + 1) }); i = end + 2; continue; }
    }
    if (depth < 4 && rest.startsWith('~~')) {
      const end = s.indexOf('~~', i + 2);
      if (end > i + 2) { flush(); out.push({ t: 's', c: inline(s.slice(i + 2, end), depth + 1) }); i = end + 2; continue; }
    }
    if (depth < 4 && (ch === '*' || ch === '_') && s[i + 1] && s[i + 1] !== ' ' && s[i + 1] !== ch) {
      // _ only at a word boundary, so snake_case_names stay as they are.
      const prev = i > 0 ? s[i - 1] : ' ';
      if (ch === '*' || /[\s(]/.test(prev)) {
        let end = s.indexOf(ch, i + 1);
        while (end > 0 && s[end - 1] === ' ') end = s.indexOf(ch, end + 1);
        const after = end > 0 ? s[end + 1] : '';
        if (end > i + 1 && (ch === '*' || !after || /[\s.,;:!?)]/.test(after))) {
          flush(); out.push({ t: 'i', c: inline(s.slice(i + 1, end), depth + 1) }); i = end + 1; continue;
        }
      }
    }
    if (ch === '!' && s[i + 1] === '[') {
      const m = /^!\[([^\]]{0,300})\]\(\s*([^)\s]+)(?:\s+"[^"]*")?\s*\)/.exec(rest);
      if (m) {
        flush();
        const safe = safeSrc(m[2]);
        if (safe) out.push({ t: 'img', alt: m[1], src: safe.src, kind: safe.kind, id: safe.id || null });
        else out.push({ t: 'text', v: m[1] ? `[image: ${m[1]}]` : '[image]' });
        i += m[0].length; continue;
      }
    }
    if (ch === '[') {
      const m = /^\[([^\]]{1,500})\]\(\s*([^)\s]+)\s*\)/.exec(rest);
      if (m) {
        flush();
        const safe = safeHref(m[2]);
        const label = depth < 4 ? inline(m[1], depth + 1) : [{ t: 'text', v: m[1] }];
        if (safe) out.push({ t: 'link', href: safe.href, kind: safe.kind, id: safe.id || null, anchor: safe.anchor || null, c: label });
        else out.push(...label);
        i += m[0].length; continue;
      }
    }
    // A bare web address.
    if ((ch === 'h' || ch === 'H') && /^https?:\/\//i.test(rest) && (i === 0 || /[\s(]/.test(s[i - 1]))) {
      const m = /^https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"]/i.exec(rest);
      if (m && safeHref(m[0])) {
        flush(); out.push({ t: 'link', href: m[0], kind: 'web', id: null, anchor: null, c: [{ t: 'text', v: m[0] }] });
        i += m[0].length; continue;
      }
    }
    if (ch === '\n') { flush(); out.push({ t: 'br' }); i += 1; continue; }
    text += ch;
    i += 1;
  }
  flush();
  return out;
}

/* ------------------------------------------------------------------ blocks */

const indentOf = (line) => (/^ */.exec(line)[0].length);
const LIST_RE = /^( *)([-*+]|\d{1,3}[.)])\s+(.*)$/;
const FENCE_RE = /^ *(```|~~~)\s*([A-Za-z0-9_+-]*)\s*$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

function splitRow(line) {
  let l = line.trim();
  if (l.startsWith('|')) l = l.slice(1);
  if (l.endsWith('|') && !l.endsWith('\\|')) l = l.slice(0, -1);
  const cells = [];
  let cur = '';
  for (let i = 0; i < l.length; i += 1) {
    if (l[i] === '\\' && l[i + 1] === '|') { cur += '|'; i += 1; continue; }
    if (l[i] === '|') { cells.push(cur.trim()); cur = ''; continue; }
    cur += l[i];
  }
  cells.push(cur.trim());
  return cells;
}

/** A ```flow block: one step a line, "Step | Who | When" (Who and When optional). */
function flowSteps(text) {
  return String(text || '').split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 60).map((l) => {
    const [title, who, when] = splitRow(l.includes('|') ? l : `${l}|`);
    return { title: inline(title || ''), who: who || '', when: when || '' };
  });
}

/**
 * The blocks of an article. `depth` stops a pathological nesting from
 * recursing for ever; past it, text is simply text.
 */
function parse(markdown, depth = 0) {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n');
  const out = [];
  let i = 0;
  let para = [];
  const flushPara = () => {
    if (!para.length) return;
    out.push({ t: 'p', c: inline(para.join('\n')) });
    para = [];
  };
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) { flushPara(); i += 1; continue; }

    // Fenced code (and flows).
    const fence = FENCE_RE.exec(line);
    if (fence) {
      flushPara();
      const body = [];
      i += 1;
      while (i < lines.length && !new RegExp(`^ *${fence[1]}\\s*$`).test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1;
      const lang = (fence[2] || '').toLowerCase();
      if (lang === 'flow') out.push({ t: 'flow', steps: flowSteps(body.join('\n')) });
      else out.push({ t: 'code', lang: lang.slice(0, 20), v: body.join('\n') });
      continue;
    }

    const h = /^ {0,3}(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    if (h) {
      flushPara();
      out.push({ t: 'h', level: Math.min(h[1].length, 4), c: inline(h[2]), text: h[2] });
      i += 1; continue;
    }

    if (/^ {0,3}([-*_])( *\1){2,} *$/.test(line)) {
      flushPara(); out.push({ t: 'hr' }); i += 1; continue;
    }

    // Quotes and callouts: every line starting with ">".
    if (/^ {0,3}>/.test(line)) {
      flushPara();
      const body = [];
      while (i < lines.length && /^ {0,3}>/.test(lines[i])) { body.push(lines[i].replace(/^ {0,3}> ?/, '')); i += 1; }
      const call = /^\[!(note|tip|warning|danger|caution|important|info)\]\s*(.*)$/i.exec(body[0] || '');
      if (call) {
        const raw = call[1].toLowerCase();
        const kind = raw === 'caution' ? 'warning' : raw === 'important' || raw === 'info' ? 'note' : raw;
        const restLines = [call[2], ...body.slice(1)].join('\n');
        out.push({ t: 'callout', kind: CALLOUTS.includes(kind) ? kind : 'note', c: depth < 6 ? parse(restLines, depth + 1) : [] });
      } else {
        out.push({ t: 'quote', c: depth < 6 ? parse(body.join('\n'), depth + 1) : [] });
      }
      continue;
    }

    // Tables: a header row, then the --- row.
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      flushPara();
      const head = splitRow(line);
      const align = splitRow(lines[i + 1]).map((c) => (/^:-+:$/.test(c) ? 'center' : /-+:$/.test(c) ? 'right' : 'left'));
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
        const cells = splitRow(lines[i]);
        rows.push(head.map((_, k) => inline(cells[k] || '')));
        i += 1;
        if (rows.length >= 500) break;
      }
      out.push({ t: 'table', head: head.map((c) => inline(c)), align: head.map((_, k) => align[k] || 'left'), rows });
      continue;
    }

    // Lists: items at this indent, each with the lines indented under it.
    const lm = LIST_RE.exec(line);
    if (lm && (para.length === 0 || indentOf(line) <= 3)) {
      flushPara();
      const base = lm[1].length;
      const ordered = /\d/.test(lm[2]);
      const start = ordered ? parseInt(lm[2], 10) : 1;
      const items = [];
      while (i < lines.length) {
        const m = LIST_RE.exec(lines[i]);
        if (!m || m[1].length !== base || /\d/.test(m[2]) !== ordered) break;
        let first = m[3];
        let task = null;
        const tm = /^\[([ xX])\]\s+(.*)$/.exec(first);
        if (tm) { task = tm[1].toLowerCase() === 'x'; first = tm[2]; }
        const content = [first];
        const contentIndent = m[1].length + m[2].length + 1;
        i += 1;
        // Lines that belong to this item: indented past its marker, or blank
        // lines followed by such a line.
        while (i < lines.length) {
          const l = lines[i];
          if (!l.trim()) {
            const next = lines.slice(i + 1).find((x) => x.trim());
            if (next !== undefined && indentOf(next) > base) { content.push(''); i += 1; continue; }
            break;
          }
          if (indentOf(l) > base) { content.push(l.slice(Math.min(indentOf(l), contentIndent))); i += 1; continue; }
          // A lazy continuation of the item's first paragraph.
          if (!LIST_RE.test(l) && !FENCE_RE.test(l) && !/^ {0,3}(#|>|\|)/.test(l) && content.length === 1) {
            content[0] += `\n${l.trim()}`; i += 1; continue;
          }
          break;
        }
        const firstLine = content[0];
        const more = content.slice(1).join('\n');
        items.push({
          task,
          c: inline(firstLine),
          blocks: more.trim() && depth < 6 ? parse(more, depth + 1) : []
        });
        while (i < lines.length && !lines[i].trim()) {
          const next = lines.slice(i + 1).find((x) => x.trim());
          const nm = next !== undefined ? LIST_RE.exec(next) : null;
          if (nm && nm[1].length === base && /\d/.test(nm[2]) === ordered) i += 1; else break;
        }
        if (items.length >= 500) break;
      }
      out.push({ t: 'list', ordered, start, items });
      continue;
    }

    para.push(trimmed);
    i += 1;
  }
  flushPara();
  // Heading ids, unique within the article.
  if (depth === 0) {
    const seen = new Map();
    eachBlock(out, (b) => {
      if (b.t !== 'h') return;
      const base = slug(plainOf(b.c));
      const n = (seen.get(base) || 0) + 1;
      seen.set(base, n);
      b.id = n === 1 ? base : `${base}-${n}`;
    });
  }
  return out;
}

/* ----------------------------------------------------------------- readers */

/** The plain words of some inline runs. */
function plainOf(runs) {
  return (runs || []).map((r) => {
    if (r.t === 'text' || r.t === 'code' || r.t === 'ticket' || r.t === 'asset') return r.v;
    if (r.t === 'br') return ' ';
    if (r.t === 'img') return r.alt || '';
    return plainOf(r.c);
  }).join('');
}

/** Walk every block, depth first. */
function eachBlock(blocks, fn) {
  for (const b of blocks || []) {
    fn(b);
    if (b.t === 'callout' || b.t === 'quote') eachBlock(b.c, fn);
    if (b.t === 'list') for (const it of b.items) eachBlock(it.blocks, fn);
  }
}

/** Every inline run list in the tree. */
function eachRuns(blocks, fn) {
  eachBlock(blocks, (b) => {
    if (b.t === 'p' || b.t === 'h') fn(b.c);
    if (b.t === 'list') for (const it of b.items) fn(it.c);
    if (b.t === 'table') { b.head.forEach(fn); b.rows.forEach((r) => r.forEach(fn)); }
    if (b.t === 'flow') b.steps.forEach((s) => fn(s.title));
  });
}

/** The article as plain text: for search, snippets and summaries. */
function plainText(markdown) {
  const parts = [];
  eachBlock(parse(markdown), (b) => {
    if (b.t === 'p' || b.t === 'h') parts.push(plainOf(b.c));
    if (b.t === 'code') parts.push(b.v);
    if (b.t === 'list') for (const it of b.items) parts.push(plainOf(it.c));
    if (b.t === 'table') { parts.push(b.head.map(plainOf).join(' ')); for (const r of b.rows) parts.push(r.map(plainOf).join(' ')); }
    if (b.t === 'flow') for (const s of b.steps) parts.push([plainOf(s.title), s.who, s.when].join(' '));
  });
  return parts.join('\n').replace(/[ \t]+/g, ' ').trim();
}

/** What an article points at: other articles, tickets, asset tags, its own files. */
function refs(markdown) {
  const docs = new Set(); const tickets = new Set(); const assets = new Set(); const files = new Set();
  const walk = (runs) => {
    for (const r of runs || []) {
      if (r.t === 'link' && r.kind === 'doc') docs.add(r.id);
      if ((r.t === 'img' || r.t === 'link') && r.kind === 'file') files.add(r.id);
      if (r.t === 'ticket') tickets.add(r.v);
      if (r.t === 'asset') assets.add(r.v);
      if (r.c) walk(r.c);
    }
  };
  eachRuns(parse(markdown), walk);
  return { docs: [...docs], tickets: [...tickets], assets: [...assets], files: [...files] };
}

/** The headings, for a table of contents. */
function toc(blocks) {
  const out = [];
  eachBlock(blocks, (b) => { if (b.t === 'h' && b.id) out.push({ id: b.id, level: b.level, text: plainOf(b.c) }); });
  return out;
}

/** The runbook's steps: every task item, in order. */
function taskCount(blocks) {
  let n = 0;
  eachBlock(blocks, (b) => { if (b.t === 'list') for (const it of b.items) if (it.task !== null) n += 1; });
  return n;
}

/* ------------------------------------------------------------ the guard */

/*
 * Things that look like a credential. A warning, not a filter: the author is
 * told what was found and why it should live in the password manager, and may
 * save anyway (the server records that they did). Values that are clearly a
 * pointer rather than a secret - "password: see the vault", "<your password>"
 * - are not flagged.
 */
const SECRET_WORDS = 'pass(?:word|phrase|code)?|pwd|passwd|pin|secret|client[ _-]?secret|api[ _-]?key|access[ _-]?key|'
  + 'secret[ _-]?key|auth[ _-]?token|access[ _-]?token|token|psk|wpa2?[ _-]?key|wi-?fi[ _-]?(?:key|password)|'
  + 'recovery[ _-]?(?:key|code)|licen[cs]e[ _-]?key|product[ _-]?key';
const SECRET_PATTERNS = [
  // The specific shapes first: one finding per line, and the most telling one.
  { kind: 'private-key', label: 'a private key', re: /-----BEGIN [A-Z ]*PRIVATE KEY-----/g },
  { kind: 'aws-key', label: 'an AWS access key', re: /\b(AKIA[0-9A-Z]{16})\b/g },
  { kind: 'token', label: 'an access token', re: /\b((?:ghp|gho|ghs|github_pat|xox[abprs]|sk_live|sk|rk_live|pk_live)[-_][A-Za-z0-9_]{16,})\b/g },
  { kind: 'jwt', label: 'a sign-in token (JWT)', re: /\b(eyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,})\b/g },
  { kind: 'connection', label: 'a connection string with a password', re: /\b(?:password|pwd)=([^;\s]{3,})/gi },
  { kind: 'url-password', label: 'a password inside an address', re: /\b[a-z][a-z0-9+.-]*:\/\/[^\s:/@]+:([^\s@/]{3,})@/gi },
  { kind: 'licence', label: 'a product key', re: /\b([A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5})\b/g },
  { kind: 'password', label: 'a password or key written out',
    re: new RegExp(`\\b(?:${SECRET_WORDS})\\b\\s*(?:is|=|:|-)\\s*["']?([^\\s"'<>]{4,})`, 'gi') }
];
const POINTER_RE = /^(see|in|from|stored|kept|ask|n\/a|none|null|the|your|our|is|on|via|at|\*+|x+|\.+|•+|redacted|hidden|\[|<|\{|\$\{)/i;

/** [{ kind, label, line, excerpt }] - the excerpt masked so the warning does not repeat the secret. */
function secrets(markdown) {
  const text = String(markdown || '');
  const found = [];
  const seen = new Set();
  for (const p of SECRET_PATTERNS) {
    p.re.lastIndex = 0;
    let m;
    while ((m = p.re.exec(text)) && found.length < 20) {
      const value = m[1] || m[0];
      if (p.kind === 'password' && (POINTER_RE.test(value) || /^(manager|vault|1password|bitwarden|keeper|lastpass)$/i.test(value))) continue;
      const line = text.slice(0, m.index).split('\n').length;
      const key = `${line}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const masked = value.length <= 4 ? '••••' : `${value.slice(0, 2)}${'•'.repeat(Math.min(8, value.length - 2))}`;
      const shown = m[0].replace(value, masked).slice(0, 80);
      found.push({ kind: p.kind, label: p.label, line, excerpt: shown });
    }
  }
  return found.sort((a, b) => a.line - b.line);
}

/* ---- exports ---- */
export { parse, inline, plainOf, plainText, refs, toc, taskCount, secrets, slug, safeHref, safeSrc, eachBlock };
